import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import NotificationBar from "../components/home/NotifictionBar";
import searchFormBg from "../assets/searchform-bg.jpg";

import Leftsidebar from "../components/vehicles/Leftsidebar";
import VehicleCard from "../components/VehicleCard";
import VehicleListCard from "../components/VehicleListCard";
import api from "../lib/api.ts";
import placeholderImage from "../assets/Car-place-holder-PNG.png";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import usePageMeta from "../hooks/usePageMeta.js";
import { Menu, X } from "lucide-react";

const Search = () => {
  usePageMeta("vehicles");

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Disable body scroll when mobile sidebar is open to fix mobile scrolling issues
  useEffect(() => {
    const layoutContainer = document.getElementById("main-layout-container");
    if (isSidebarOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
      if (layoutContainer) {
        layoutContainer.style.overflow = "hidden";
        layoutContainer.style.height = "100vh";
      }
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
      if (layoutContainer) {
        layoutContainer.style.overflow = "";
        layoutContainer.style.height = "";
      }
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
      if (layoutContainer) {
        layoutContainer.style.overflow = "";
        layoutContainer.style.height = "";
      }
    };
  }, [isSidebarOpen]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("search") || "");
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  // Extract auctionId and laneId from URL parameters
  const auctionId = searchParams.get("auctionId");
  const laneId = searchParams.get("laneId");

  // Reset URL params processed flag when URL parameters change
  useEffect(() => {
    setUrlParamsProcessed(false);
  }, [auctionId, laneId]);

  // console.log("searchParams", searchParams);
  // console.log("auctionId", auctionId);
  // console.log("laneId", laneId);
  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setCurrentPage(1); // Reset page on search input
  };

  const handleSearch = () => {
    // Search functionality handled by API calls
  };
  // test message

  const [filters, setFilters] = useState({
    make: searchParams.get("makeId") || "",
    model: searchParams.get("modelId") || "",
    variantId: searchParams.get("variantId") || "",
    yearStart: searchParams.get("year") || "",
    yearEnd: searchParams.get("year") || "",
  });

  // State for makes and models data from API
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);

  // State for vehicles data from API
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false); // To track if we've made the initial fetch
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // State for wishlist data
  const [wishlistData, setWishlistData] = useState([]);

  // State for auctions data from API
  const [auctions, setAuctions] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState("");
  const [selectedLane, setSelectedLane] = useState("");
  const [urlError, setUrlError] = useState("");
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Sync URL parameters with filters when component mounts or URL changes
  useEffect(() => {
    const makeIdFromUrl = searchParams.get("makeId");
    const modelIdFromUrl = searchParams.get("modelId");
    const variantIdFromUrl = searchParams.get("variantId");
    const yearFromUrl = searchParams.get("year");
    if (makeIdFromUrl && makeIdFromUrl !== filters.make) {
      // Validate that the makeId exists in our makes array
      const isValidMake = makes.some(
        (make) => make.id.toString() === makeIdFromUrl,
      );
      if (isValidMake) {
        setFilters((prev) => ({
          ...prev,
          make: makeIdFromUrl,
          // Preserve URL params instead of resetting
          model: modelIdFromUrl || "",
          variantId: variantIdFromUrl || "",
          yearStart: yearFromUrl || prev.yearStart || "",
          yearEnd: yearFromUrl || prev.yearEnd || "",
        }));
      } else {
        // If invalid makeId, remove it from URL and show error
        console.warn(`Invalid makeId in URL: ${makeIdFromUrl}`);
        setUrlError(
          `Invalid make ID "${makeIdFromUrl}" found in URL. It has been removed.`,
        );
        const params = new URLSearchParams(searchParams);
        params.delete("makeId");
        const newUrl = `${window.location.pathname}${
          params.toString() ? "?" + params.toString() : ""
        }`;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [searchParams, makes]);

  // Sync modelId and variantId from URL into filters when models are loaded
  useEffect(() => {
    const modelIdFromUrl = searchParams.get("modelId");
    const variantIdFromUrl = searchParams.get("variantId");
    if (modelIdFromUrl && models.length > 0 && !filters.model) {
      const isValidModel = models.some(
        (model) => model.id.toString() === modelIdFromUrl,
      );
      if (isValidModel) {
        setFilters((prev) => ({
          ...prev,
          model: modelIdFromUrl,
          variantId: variantIdFromUrl || prev.variantId || "",
        }));
      }
    }
  }, [searchParams, models]);

  // Sync search parameter from URL with query state
  useEffect(() => {
    const searchFromUrl = searchParams.get("search");
    if (searchFromUrl !== null && searchFromUrl !== query) {
      setQuery(searchFromUrl);
    }
  }, [searchParams, query]);

  // Auto-apply featured filter from URL parameter
  useEffect(() => {
    const featuredParam = searchParams.get("featured");
    if (featuredParam !== null && sidebarRef.current) {
      const normalized = (() => {
        const val = String(featuredParam).toLowerCase();
        if (val === "1" || val === "yes" || val === "true") return "yes";
        if (val === "0" || val === "no" || val === "false") return "no";
        return "";
      })();
      // Set the featured filter in the sidebar to trigger parent sync
      if (typeof sidebarRef.current.setFeaturedFilter === "function") {
        sidebarRef.current.setFeaturedFilter(normalized);
      }
    }
  }, [searchParams]);

  // Handle auctionId and laneId from URL parameters
  useEffect(() => {
    if (auctionId && auctions.length > 0) {
      // Find the auction and set it as selected
      const foundAuction = auctions.find(
        (auction) => auction.id.toString() === auctionId,
      );
      if (foundAuction) {
        setSelectedAuction(auctionId);
      }
    }
  }, [auctionId, auctions]);

  useEffect(() => {
    if (laneId && selectedAuction && auctions.length > 0) {
      // Find the lane within the selected auction and set it as selected
      const selectedAuctionData = auctions.find(
        (auction) => auction.id.toString() === selectedAuction,
      );
      if (selectedAuctionData?.auctionLanes) {
        const foundLane = selectedAuctionData.auctionLanes.find((lane) => {
          const laneIdMatch = lane.laneId?.toString() === laneId;
          const idMatch = lane.id?.toString() === laneId;

          return laneIdMatch || idMatch;
        });
        if (foundLane) {
          // Use the original laneId from URL, not the found lane's ID
          setSelectedLane(laneId);
        }
      }
    }
  }, [laneId, selectedAuction, auctions]);

  // Mark URL parameters as processed when both auction and lane are set (or when no URL params exist)
  useEffect(() => {
    if (auctions.length > 0) {
      if (auctionId && laneId) {
        // Both auctionId and laneId are in URL - wait for both to be processed
        if (selectedAuction && selectedLane) {
          setUrlParamsProcessed(true);
        }
      } else if (auctionId && !laneId) {
        // Only auctionId is in URL - wait for auction to be processed
        if (selectedAuction) {
          setUrlParamsProcessed(true);
        }
      } else {
        // No URL parameters - can proceed immediately
        setUrlParamsProcessed(true);
      }
    }
  }, [auctionId, laneId, selectedAuction, selectedLane, auctions.length]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (filters.make) {
      params.set("makeId", filters.make);
    } else {
      params.delete("makeId");
    }

    // Update URL with auction and lane parameters
    if (selectedAuction) {
      params.set("auctionId", selectedAuction);
    } else {
      params.delete("auctionId");
    }

    if (selectedLane) {
      params.set("laneId", selectedLane);
    } else {
      params.delete("laneId");
    }

    // Update URL without page reload
    const newUrl = `${window.location.pathname}${
      params.toString() ? "?" + params.toString() : ""
    }`;
    window.history.replaceState({}, "", newUrl);
  }, [filters.make, selectedAuction, selectedLane, searchParams]);

  // Fetch makes data from API
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await api.get("/makes");
        setMakes(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching makes:", error);
        setMakes([]); // Set empty array on error
      }
    };

    fetchMakes();
  }, []);

  // Fetch auctions data from API
  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const response = await api.get("/auctions/paginated", {
          params: {
            type: "upcomming,live",
            orderBy: "startDate",
            order: "ASC",
            "filters[isActive]": true,
          },
        });
        const auctionsData = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        // Process auctions to extract lanes from the new API structure
        const processedAuctions = auctionsData.map((auction) => {
          // Extract lanes from auctionLanes array
          let lanes = [];
          if (auction.auctionLanes && Array.isArray(auction.auctionLanes)) {
            lanes = auction.auctionLanes.map((lane) => {
              // Get the first vehicle's run number from auctionLaneVehicles
              const firstVehicle = lane.auctionLaneVehicles?.[0];
              const runNo = firstVehicle?.runNo || 0;

              return {
                id: lane.id, // This is the auctionLaneId we'll use for filtering vehicles
                laneId: lane.laneId, // This is the actual lane identifier from the lane table
                auctionId: lane.auctionId || auction.id,
                runNo: runNo,
                lane: lane.lane, // Full lane object with name, etc.
                auction: auction,
              };
            });
          }

          return {
            ...auction,
            auctionLanes: lanes,
          };
        });

        setAuctions(processedAuctions);
      } catch (error) {
        console.error("Error fetching auctions:", error);
        setAuctions([]); // Set empty array on error
      }
    };

    fetchAuctions();
  }, []);

  // Fetch wishlist data from API
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id) {
        setWishlistData([]);
        return;
      }

      try {
        const response = await api.get("/wishlist");
        const wishlist = response.data?.data || response.data;

        if (wishlist && Array.isArray(wishlist)) {
          setWishlistData(wishlist);
        } else {
          setWishlistData([]);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
        setWishlistData([]);
      }
    };

    fetchWishlist();
  }, [user?.id]);

  // Fetch models data when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!filters.make) {
        setModels([]);
        return;
      }

      try {
        const response = await api.get(`/models?makeId=${filters.make}`);
        setModels(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching models:", error);
        setModels([]); // Set empty array on error
      }
    };

    fetchModels();
  }, [filters.make, searchParams]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1); // Reset page on filter change

    // If make changes, reset model
    if (name === "make") {
      setFilters({ ...filters, [name]: value, model: "" });
    } else {
      setFilters({ ...filters, [name]: value });
    }
  };

  const [activeFilters, setActiveFilters] = useState({});

  const [sidebarFilters, setSidebarFilters] = useState({});
  const [sidebarFilterData, setSidebarFilterData] = useState({});
  const sidebarRef = useRef();

  // View toggle state
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [sortBy, setSortBy] = useState("run-asc");

  // Calculate pagination info for display
  const displayCount = totalVehicles;
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, displayCount);
  const currentVehicles = vehicles; // API already returns paginated results

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Removed scroll to top for load more functionality
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1); // Reset to first page
  };

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1); // Reset to first page
  };

  // Handle auction change
  const handleAuctionChange = (newAuction) => {
    // console.log("=== AUCTION CHANGE ===");
    // console.log("Previous Auction ID:", selectedAuction);
    // console.log("New Auction ID:", newAuction);

    if (newAuction) {
      const selectedAuctionData = auctions.find(
        (auction) => auction.id.toString() === newAuction,
      );
      if (selectedAuctionData) {
        // console.log("New Auction Data:", selectedAuctionData);
        // console.log(
        //   "Available Lanes Count:",
        //   selectedAuctionData.auctionLanes?.length || 0
        // );
        if (
          selectedAuctionData.auctionLanes &&
          selectedAuctionData.auctionLanes.length > 0
        ) {
          console.log("=== ALL LANES IN SELECTED AUCTION ===");
          console.log(
            "Auction Name:",
            selectedAuctionData.name || selectedAuctionData.auction,
          );
          console.log("Total Lanes:", selectedAuctionData.auctionLanes.length);
          console.log("Lanes Details:");

          // Create a table-like format for better readability
          console.table(
            selectedAuctionData.auctionLanes.map((lane, index) => ({
              "#": index + 1,
              "Auction Lane ID": lane.id,
              "Lane ID": lane.laneId,
              "Lane Name": lane.lane?.name || `Lane ${lane.laneId}`,
              "Run Number": lane.runNo || "N/A",
              "Vehicle Count": lane.auctionLaneVehicles?.length || 0,
              "Auction ID": lane.auctionId || selectedAuctionData.id,
            })),
          );

          // Also log individual lane details for debugging
          selectedAuctionData.auctionLanes.forEach((lane, index) => {
            console.log(`Lane ${index + 1} Details:`, {
              auctionLaneId: lane.id,
              laneId: lane.laneId,
              laneName: lane.lane?.name || `Lane ${lane.laneId}`,
              runNo: lane.runNo,
              vehicleCount: lane.auctionLaneVehicles?.length || 0,
              auctionId: lane.auctionId || selectedAuctionData.id,
              fullLaneObject: lane,
            });
          });
          console.log("=====================================");
        } else {
          console.log("No lanes available for this auction");
        }
      }
    } else {
      console.log("Auction deselected - showing all auctions");
    }
    console.log("=====================");

    setSelectedAuction(newAuction);
    setSelectedLane(""); // Reset lane when auction changes
    setCurrentPage(1); // Reset to first page
  };

  // Handle lane selection
  const handleLaneSelect = (laneId) => {
    const newSelectedLane = selectedLane === laneId ? "" : laneId;
    console.log("=== LANE SELECTION ===");
    console.log("Previous Lane ID:", selectedLane);
    console.log("New Lane ID:", newSelectedLane);
    console.log("Action:", newSelectedLane ? "Selected" : "Deselected");

    // Find and log lane details
    if (newSelectedLane && selectedAuction) {
      const selectedAuctionData = auctions.find(
        (auction) => auction.id.toString() === selectedAuction,
      );
      if (selectedAuctionData?.auctionLanes) {
        const selectedLaneData = selectedAuctionData.auctionLanes.find(
          (lane) =>
            lane.id?.toString() === newSelectedLane ||
            lane.laneId?.toString() === newSelectedLane,
        );
        if (selectedLaneData) {
          console.log("Selected Lane Details:", {
            id: selectedLaneData.id,
            laneId: selectedLaneData.laneId,
            laneName:
              selectedLaneData.lane?.name || `Lane ${selectedLaneData.laneId}`,
            runNo: selectedLaneData.runNo,
            vehicleCount: selectedLaneData.auctionLaneVehicles?.length || 0,
          });
        }
      }
    }
    console.log("=====================");

    setSelectedLane(newSelectedLane);
  };

  // Handle sidebar filter changes
  const handleSidebarFiltersChange = (sidebarFilters, filterData) => {
    setSidebarFilters(sidebarFilters);
    setSidebarFilterData(filterData);
    setCurrentPage(1); // Reset to first page when sidebar filters change

    // Sync make, model, variant, year to main filters
    setFilters((prev) => ({
      ...prev,
      make: Array.isArray(sidebarFilters.make)
        ? sidebarFilters.make.join(",")
        : sidebarFilters.make || "",
      model: Array.isArray(sidebarFilters.model)
        ? sidebarFilters.model.join(",")
        : sidebarFilters.model || "",
      variantId: Array.isArray(sidebarFilters.variant)
        ? sidebarFilters.variant.join(",")
        : sidebarFilters.variant || "",
      yearStart: Array.isArray(sidebarFilters.year)
        ? sidebarFilters.year.join(",")
        : sidebarFilters.year || "",
    }));
  };

  // Fetch vehicles data from API
  useEffect(() => {
    const fetchVehicles = async () => {
      // Don't fetch vehicles until URL parameters are processed (if they exist)
      if ((auctionId || laneId) && !urlParamsProcessed) {
        return;
      }

      try {
        // Prepare API parameters with proper sorting and filtering
        const apiParams = {
          type: "upcomming",
          page: currentPage,
          limit: pageSize,
          search: query || "",
          // Main filters

          "filters[ProductStatus]": "2,3,4",
          "filters[makeName]": filters.make || "",
          "filters[MakeModelName]": filters.model || "",
          "filters[variantId]": filters.variantId || "",
          "filters[yearStart]": filters.yearStart || "",
          "filters[yearEnd]": filters.yearEnd || "",
          "filters[search]": query || "",
          // Sidebar filters from the left sidebar
          // Note: Featured filter is handled client-side after API response
          ...(sidebarFilters.bodyTypes?.length && {
            "filters[BodyType]": sidebarFilters.bodyTypes.join(","),
          }),
          ...(sidebarFilters.drivelineTypes?.length && {
            "filters[DriveLineType]": sidebarFilters.drivelineTypes.join(","),
          }),
          ...(sidebarFilters.transmissionTypes?.length && {
            "filters[TransmissionType]":
              sidebarFilters.transmissionTypes.join(","),
          }),
          ...(sidebarFilters.fuelTypes?.length && {
            "filters[fuelType]": sidebarFilters.fuelTypes.join(","),
          }),
          ...(sidebarFilters.engines?.length && {
            "filters[engine]": sidebarFilters.engines.join(","),
          }),
          ...(sidebarFilters.highlights?.length && {
            "filters[Highlight]": sidebarFilters.highlights.join(","),
          }),
          ...(sidebarFilters.cylinders?.length && {
            "filters[cylinder]": sidebarFilters.cylinders.join(","),
          }),
          ...(sidebarFilters.exteriorColors?.length && {
            "filters[ExteriorColor]": sidebarFilters.exteriorColors.join(","),
          }),
          ...(sidebarFilters.interiorColors?.length && {
            "filters[InteriorColor]": sidebarFilters.interiorColors.join(","),
          }),
          ...(sidebarFilters.specifications?.length && {
            "filters[specification]": sidebarFilters.specifications.join(","),
          }),
          ...(sidebarFilters.primaryDamageTypes?.length && {
            "filters[primaryDamageType]":
              sidebarFilters.primaryDamageTypes.join(","),
          }),
          ...(sidebarFilters.price &&
            (sidebarFilters.price.min !== 0 ||
              sidebarFilters.price.max !== 1000000) && {
              "filters[minPrice]": sidebarFilters.price.min,
              "filters[maxPrice]": sidebarFilters.price.max,
            }),
          // Auction filter
          ...(selectedAuction && {
            "filters[auction]": selectedAuction,
          }),
          // Lane filter - use auctionLaneId for filtering
          ...(selectedLane && {
            "filters[lane]": selectedLane,
          }),
        };

        // Add sorting parameters based on sortBy selection
        if (sortBy) {
          switch (sortBy) {
            case "run-asc":
              apiParams.orderBy = "runNo";
              apiParams.order = "asc";
              break;
            case "run-desc":
              apiParams.orderBy = "runNo";
              apiParams.order = "desc";
              break;
            case "id-desc":
              apiParams.orderBy = "id";
              apiParams.order = "desc";
              break;
            case "id-asc":
              apiParams.orderBy = "id";
              apiParams.order = "asc";
              break;
            case "price-low":
              apiParams.orderBy = "startingBid";
              apiParams.order = "asc";
              break;
            case "price-high":
              apiParams.orderBy = "startingBid";
              apiParams.order = "desc";
              break;
            case "year-new":
              apiParams.orderBy = "year";
              apiParams.order = "desc";
              break;
            case "year-old":
              apiParams.orderBy = "year";
              apiParams.order = "asc";
              break;
            case "mileage-low":
              apiParams.orderBy = "mileage";
              apiParams.order = "asc";
              break;
            case "mileage-high":
              apiParams.orderBy = "mileage";
              apiParams.order = "desc";
              break;
            default:
              apiParams.orderBy = "id";
              apiParams.order = "desc";
          }
        } else {
          apiParams.orderBy = "id";
          apiParams.order = "desc";
        }

        const response = await api.get("/products/paginated", {
          params: apiParams,
        });

        // Extract pagination data from API response
        const apiData = response.data;
        const totalCount =
          apiData.total || apiData.totalCount || apiData.count || 0;
        const totalPagesFromApi =
          apiData.totalPages ||
          apiData.last_page ||
          Math.ceil(totalCount / pageSize);

        setTotalVehicles(totalCount);
        setTotalPages(totalPagesFromApi);

        // Helper function to safely extract string values
        const safeString = (value, fallback = "") => {
          if (value === null || value === undefined) return fallback;
          if (typeof value === "object" && value.name) return value.name;
          if (typeof value === "string") return value;
          if (typeof value === "number") return value.toString();
          return fallback;
        };

        // Map API response to match sampleVehicles structure
        const mappedVehicles = Array.isArray(response.data?.data)
          ? response.data.data.map((product) => {
              // Helper to safely get string values (your existing safeString)
              // Assuming safeString is defined somewhere globally or imported

              return {
                // ────────────────────────────────────────────────
                // BASIC & CORE FIELDS
                // ────────────────────────────────────────────────
                id: product.id,
                title: safeString(product?.name),
                slug: safeString(product?.slug),
                lotNo: safeString(product?.lotNumber),
                condition: safeString(product?.primaryDamageType?.name),
                miles: Number(product?.mileage || 0),
                startBidding: Number(product?.startingBid || 0),
                latestBid: Number(product?.latestBid || 0),
                startingBid: Number(product?.startingBid || 0), // backward compat
                currentBid: Number(product?.latestBid || 0), // backward compat
                buyNowPrice: Number(product?.buyNowPrice || 0),

                // Image - robust fallback chain
                image: safeString(
                  Array.isArray(product?.images) && product?.images?.length > 0
                    ? product.images[0]?.imageUrl ||
                        product.images[0]?.image ||
                        product.images[0]?.url ||
                        product.images[0]
                    : placeholderImage,
                ),

                isRunAndDrive: Boolean(
                  product.isRunAndDrive !== undefined
                    ? product.isRunAndDrive
                    : product.run_and_drive !== undefined
                      ? product.run_and_drive
                      : safeString(product?.condition || "")
                          .toLowerCase()
                          .includes("run"),
                ),

                // ────────────────────────────────────────────────
                // YEAR / MAKE / MODEL / VIN / SELLER
                // ────────────────────────────────────────────────
                year: safeString(
                  product?.year || product?.Year || product?.manufacture_year,
                ),
                make: safeString(product?.make),
                model: safeString(product?.model),
                vin: (() => {
                  const vin = safeString(product?.vinNumber);
                  return vin && vin.length > 4 ? "*****" + vin.slice(-4) : vin;
                })(),
                sellerName: safeString(product?.seller?.user?.name),
                auctionStatus: safeString(
                  product?.auctionStatus || product?.auction_status,
                ),

                // ────────────────────────────────────────────────
                // VALUE & FEATURED
                // ────────────────────────────────────────────────
                estimatedRetailValue: Number(
                  product?.estimatedRetailValue ||
                    product?.estimated_retail_value ||
                    0,
                ),
                isFeatured: Number(product?.isFeatured || 0),

                // ────────────────────────────────────────────────
                // CLASSIFICATION FIELDS → KEEP NESTED OBJECTS (main fix)
                // ────────────────────────────────────────────────
                DriveLineType: product?.DriveLineType || null,
                TransmissionType: product?.TransmissionType || null,
                BodyType: product?.BodyType || null,
                ExteriorColor: product?.ExteriorColor || null,
                InteriorColor: product?.InteriorColor || null,
                PrimaryDamageType: product?.primaryDamageType || null,
                fuelType: product?.fuelType || null,
                engine: product?.engine || null,
                cylinder: product?.cylinder || null,

                // Flat fallbacks (only use if you must – prefer nested .name in VehicleCard)
                driveLineType: safeString(
                  product?.DriveLineType?.name || product?.driveLineType || "-",
                ),
                transmissionType: safeString(
                  product?.TransmissionType?.name ||
                    product?.transmissionType ||
                    product?.transmission ||
                    "-",
                ),
                bodyType: safeString(
                  product?.BodyType?.name || product?.bodyType || "-",
                ),
                exteriorColor: safeString(
                  product?.ExteriorColor?.name || product?.exteriorColor || "-",
                ),
                interiorColor: safeString(
                  product?.InteriorColor?.name || product?.interiorColor || "-",
                ),
                primaryDamageType: safeString(
                  product?.primaryDamageType?.name ||
                    product?.primaryDamageType ||
                    product?.condition ||
                    "-",
                ),

                // IDs (preserved)
                drivelineTypeId:
                  product?.DriveLineType?.id ||
                  product?.driveLineTypeId ||
                  product?.drivelineTypeId ||
                  null,
                transmissionTypeId:
                  product?.TransmissionType?.id ||
                  product?.transmissionTypeId ||
                  product?.transmission_type_id ||
                  null,
                bodyTypeId:
                  product?.BodyType?.id ||
                  product?.bodyTypeId ||
                  product?.body_type_id ||
                  null,
                exteriorColorId:
                  product?.ExteriorColor?.id ||
                  product?.exteriorColorId ||
                  product?.exterior_color_id ||
                  null,
                interiorColorId:
                  product?.InteriorColor?.id ||
                  product?.interiorColorId ||
                  product?.interior_color_id ||
                  null,
                primaryDamageTypeId:
                  product?.primaryDamageType?.id ||
                  product?.primaryDamageTypeId ||
                  product?.primary_damage_type_id ||
                  null,
                fuelTypeId:
                  product?.fuelType?.id ||
                  product?.fuelTypeId ||
                  product?.fuel_type_id ||
                  null,
                engineId:
                  product?.engine?.id ||
                  product?.engineId ||
                  product?.engine_id ||
                  null,
                cylinderId:
                  product?.cylinder?.id ||
                  product?.cylinderId ||
                  product?.cylinder_id ||
                  null,

                // ────────────────────────────────────────────────
                // OTHER IMPORTANT FIELDS
                // ────────────────────────────────────────────────
                makeId:
                  product?.makeId ||
                  product?.make_id ||
                  product?.MakeModel?.makeId ||
                  product?.MakeModel?.make?.id ||
                  null,
                modelId:
                  product?.modelId ||
                  product?.model_id ||
                  product?.MakeModel?.id ||
                  null,
                variantId:
                  product?.variantId ||
                  product?.variant_id ||
                  product?.variant?.id ||
                  null,
                marketValue: Number(product?.marketValue || 0),
                bidIncrement: Number(product?.bidIncrement || 0),
                reservedPrice: Number(product?.reservedPrice || 0),

                // Status objects (keep full objects)
                ProductStatus: product?.ProductStatus || null,
                ProductAuctionStatus: product?.ProductAuctionStatus || null,

                productAuctionStatus: safeString(
                  product?.ProductAuctionStatus?.name || "",
                ),
                productPaymentStatus: safeString(
                  product?.ProductPaymentStatus?.name || "",
                ),
                productStatus: safeString(product?.ProductStatus?.name || ""),

                // Auction / Lane info
               
                runNo: (() => {
                  const laneVehicle = selectedLane
                    ? product?.auctionLanesVehicles?.find(
                      (item) =>
                        item?.auctionLane?.id?.toString() === selectedLane?.toString() ||
                        item?.auctionLaneId?.toString() === selectedLane?.toString() ||
                        item?.id?.toString() === selectedLane?.toString()
                    )
                    : product?.auctionLanesVehicles?.[0];

                  return Number(laneVehicle?.runNo || product?.runNo || 0);
                })(),

                auctionLaneId: (() => {
                  return (
                    product?.auctionLanesVehicles?.[0]?.auctionLane?.id ||
                    product?.auctionLanesVehicles?.[0]?.id ||
                    null
                  );
                })(),
                auctionLanesVehicles: product?.auctionLanesVehicles || [],

                // Highlights & Specifications (your existing logic - unchanged)
                highlights: (() => {
                  const sources = [
                    product?.highlights,
                    product?.Highlights,
                    product?.highlight,
                    product?.Highlight,
                  ];

                  for (const source of sources) {
                    if (Array.isArray(source) && source.length > 0) {
                      return source
                        .map((h) => {
                          if (typeof h === "object" && h !== null) {
                            return {
                              id: h.id,
                              name: safeString(h.name),
                              textColor: h.textColor || "#ffffff",
                              bgColor: h.bgColor || "#f97316",
                            };
                          }
                          return safeString(h);
                        })
                        .filter(
                          (h) => h && (typeof h === "object" ? h.name : h),
                        );
                    }
                    if (
                      typeof source === "object" &&
                      source !== null &&
                      source.name
                    ) {
                      return [
                        {
                          id: source.id,
                          name: safeString(source.name),
                          textColor: source.textColor || "#ffffff",
                          bgColor: source.bgColor || "#f97316",
                        },
                      ];
                    }
                  }
                  return [];
                })(),

                specifications: (() => {
                  const sources = [
                    product?.specifications,
                    product?.Specifications,
                    product?.specification,
                    product?.Specification,
                  ];

                  for (const source of sources) {
                    if (Array.isArray(source) && source.length > 0) {
                      return source
                        .map((s) => ({
                          id: s.id,
                          name: safeString(s.name),
                        }))
                        .filter((s) => s.id && s.name);
                    }

                    if (source && typeof source === "object" && source.name) {
                      return [
                        {
                          id: source.id,
                          name: safeString(source.name),
                        },
                      ].filter((s) => s.id && s.name);
                    }

                    if (typeof source === "string" && source.trim()) {
                      return [
                        {
                          id: source,
                          name: source,
                        },
                      ];
                    }
                  }
                  return [];
                })(),
              };
            })
          : [];

        // Apply featured filter if selected
        let filteredVehicles = mappedVehicles;
        if (sidebarFilters.featured) {
          const wantFeatured = sidebarFilters.featured === "yes";
          filteredVehicles = filteredVehicles.filter(
            (vehicle) => Boolean(vehicle.isFeatured) === wantFeatured,
          );
        }

        // Add price range filter client-side for robustness
        if (sidebarFilters.price) {
          const { min, max } = sidebarFilters.price;
          filteredVehicles = filteredVehicles.filter((vehicle) => {
            const price = Number(
              vehicle.buyNowPrice ||
                vehicle.latestBid ||
                vehicle.currentBid ||
                vehicle.startingBid ||
                vehicle.price ||
                vehicle.sellingPrice ||
                0,
            );
            // If price is 0, we might want to show it or hide it. 
            // Usually, if a filter is set, we hide 0-price items unless min is 0.
            return (min === 0 || price >= min) && (max >= 1000000 || price <= max);
          });
        }

        const sortByRunNo = (items) =>
          [...items].sort((a, b) => {
            const runA = Number(a.runNo || 0);
            const runB = Number(b.runNo || 0);

            return sortBy === "run-desc" ? runB - runA : runA - runB;
          });

        if (currentPage === 1) {
          setVehicles(sortByRunNo(filteredVehicles));
        } else {
          setVehicles((prev) => sortByRunNo([...prev, ...filteredVehicles]));
        }
      } catch (error) {
        console.error("Error fetching vehicles:", error);
        setVehicles([]); // Fallback to empty array
      } finally {
        setLoading(false);
        setHasFetched(true); // Mark that we've completed the initial fetch (even if it failed)
      }
    };

    fetchVehicles();
  }, [
    currentPage,
    pageSize,
    sortBy,
    filters.make,
    filters.model,
    filters.variantId,
    filters.yearStart,
    filters.yearEnd,
    query,
    sidebarFilters,
    selectedAuction,
    selectedLane,
    urlParamsProcessed,
    auctionId,
    laneId,
  ]);

  // Function to remove a specific sidebar filter
  const removeSidebarFilter = (filterType, filterId) => {
    // Update sidebar component state
    if (sidebarRef.current) {
      sidebarRef.current.removeFilter(filterType, filterId);
    }
  };

  // Function to clear all filters of a specific type
  const clearFilterType = (filterType) => {
    // Update sidebar component state
    if (sidebarRef.current) {
      sidebarRef.current.clearFilterType(filterType);
    }
  };

  // Get filter name by ID
  const getFilterName = (filterType, filterId) => {
    const filterArray = sidebarFilterData[filterType] || [];
    // Use == for robust matching between string and number IDs
    const filterItem = filterArray.find((item) => item.id == filterId);
    if (!filterItem) {
      // If not found in static data, try searching in specific lists if they exist
      if (filterType === "year") return filterId;
      return filterId; // Fallback to ID itself instead of "Unknown"
    }
    return filterItem.name;
  };

  // Get filter type display name
  const getFilterTypeDisplayName = (filterType) => {
    return translate(`sidebar.filters.${filterType}`) || filterType;
  };

  // Helper function to get make name by ID
  const getMakeName = (makeId) => {
    const make = makes.find((m) => m.id == makeId);
    return make ? make.name : makeId;
  };

  // Helper function to get model name by ID
  const getModelName = (modelId) => {
    const model = models.find((m) => m.id == modelId);
    return model ? model.name : modelId;
  };

  // Helper function to get lane name by ID
  const getLaneName = (laneId) => {
    // Find the lane across all auctions
    for (const auction of auctions) {
      const lane = auction.auctionLanes?.find((l) => l.id == laneId);
      if (lane) {
        return `${lane.lane?.name || `Lane ${lane.laneId}`}`;
      }
    }
    return `Lane ${laneId}`;
  };

  // Helper function to get display value for filters
  const getFilterDisplayValue = (key, value) => {
    switch (key) {
      case "make":
        return getMakeName(value);
      case "model":
        return getModelName(value);
      case "yearStart":
        return value; // Year is already a display value
      case "yearEnd":
        return value; // Year is already a display value
      default:
        return value;
    }
  };

  // Get all selected filter names for a filter type
  const getSelectedFilterNames = (filterType, selectedIds) => {
    if (filterType === "featured") {
      return selectedIds ? [selectedIds === "yes" ? "Yes" : "No"] : [];
    }
    if (filterType === "price") {
      return [`${selectedIds.min} - ${selectedIds.max}`];
    }

    // Safety check for non-array values (e.g. while transition from single to multi)
    if (!Array.isArray(selectedIds)) {
      return selectedIds ? [getFilterName(filterType, selectedIds)] : [];
    }

    if (!selectedIds || selectedIds.length === 0) return [];
    return selectedIds.map((id) => getFilterName(filterType, id));
  };

  return (
    <>
      {/* <NotificationBar /> */}
      <section className="min-h-400 mx-auto bg-page">
        {/*  <div
          className="w-full p-2 sm:p-4 flex justify-center"
          style={{
            backgroundImage: `url(${searchFormBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        > 
          {/* <div className="w-full  "> 
            {/* <SearchFilter /> 

            <form
              className={`flex flex-col lg:flex-row items-stretch lg:items-center gap-2 sm:gap-4 mb-4 sm:mb-6 w-full bg-darkGraybg bg-opacity-60 rounded-lg p-3 sm:p-6 ${isRTL ? "lg:flex-row-reverse" : ""
                }`}
              dir={isRTL ? "rtl" : "ltr"}
              lang={language}
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
                setActiveFilters({
                  query,
                  ...filters,
                });
              }}
            >
              {/* Search Input - Bigger size 
              <input
                type="text"
                className={`w-full lg:w-2/6 ${isRTL ? "pr-3 pl-3" : "px-3"
                  } py-2 rounded text-sm sm:text-base`}
                placeholder={translate("search.searchByLotVin")}
                value={query}
                onChange={handleInputChange}
                name="query"
              />

              {/* Filter Selects - Equal sizes for all 4 dropdowns 
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 w-full lg:w-3/6">
                <select
                  className={`w-full ${isRTL ? "pr-3 pl-3" : "px-3"
                    } py-2 rounded text-sm sm:text-base`}
                  name="make"
                  value={filters.make}
                  onChange={handleFilterChange}
                >
                  <option value="">{translate("search.make")}</option>
                  {Array.isArray(makes) &&
                    makes.map((make) => (
                      <option key={make.id} value={make.id}>
                        {make.name}
                      </option>
                    ))}
                </select>
                <select
                  className={`w-full ${isRTL ? "pr-3 pl-3" : "px-3"
                    } py-2 rounded text-sm sm:text-base`}
                  name="model"
                  value={filters.model}
                  onChange={handleFilterChange}
                  disabled={!filters.make}
                >
                  <option value="">
                    {!filters.make
                      ? translate("search.selectMakeFirst")
                      : translate("search.model")}
                  </option>
                  {Array.isArray(models) &&
                    models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                </select>
                <select
                  className={`w-full ${isRTL ? "pr-3 pl-3" : "px-3"
                    } py-2 rounded text-sm sm:text-base`}
                  name="yearStart"
                  value={filters.yearStart}
                  onChange={handleFilterChange}
                >
                  <option value="">{translate("search.yearFrom")}</option>
                  {Array.from({ length: 30 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
                <select
                  className={`w-full ${isRTL ? "pr-3 pl-3" : "px-3"
                    } py-2 rounded text-sm sm:text-base`}
                  name="yearEnd"
                  value={filters.yearEnd}
                  onChange={handleFilterChange}
                >
                  <option value="">{translate("search.yearTo")}</option>
                  {Array.from({ length: 30 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
              {/* Action Buttons - Smaller size to accommodate bigger search input 
              <div
                className={`flex flex-col sm:flex-row gap-2 sm:gap-4 w-full lg:w-1/6 ${isRTL ? "sm:flex-row-reverse" : ""
                  }`}
              >
                <button
                  type="submit"
                  className="w-full px-3 py-2 text-sm sm:text-base bg-primary text-white rounded hover:bg-primary-dark transition-colors"
                >
                  {translate("search.search")}
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm sm:text-base text-white bg-darkGraybg rounded hover:bg-gray-700 transition-colors"
                  onClick={() => {
                    setQuery("");
                    setFilters({
                      make: "",
                      model: "",
                      yearStart: "",
                      yearEnd: "",
                    });
                    setActiveFilters({});
                    setSidebarFilters({});
                    setSidebarFilterData({});
                    setSelectedAuction("");
                    setSelectedLane("");
                    setCurrentPage(1);
                    setSortBy("id-desc");
                    setPageSize(12);
                    setUrlError(""); // Clear URL error message

                    // Clear URL parameters
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, "", newUrl);

                    // Reset sidebar component's internal state
                    if (
                      sidebarRef.current &&
                      sidebarRef.current.resetAllFilters
                    ) {
                      sidebarRef.current.resetAllFilters();
                    }
                  }}
                >
                  {translate("search.clear")}
                </button>
              </div>
            </form>

            {/* URL Error Message 
            {urlError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{urlError}</span>
                  <button
                    onClick={() => setUrlError("")}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div className="w-full">

              {/* Active Search Filters */}
        {/* <div id="query-results" className="flex gap-1">
                  {Object.entries(activeFilters)
                    .filter(([key, value]) => value)
                    .map(([key, value], idx) => (
                      <span
                        key={idx}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {key.charAt(0).toUpperCase() + key.slice(1)}:{" "}
                        {getFilterDisplayValue(key, value)}
                      </span>
                    ))}
                </div> */}

        {/* Selected Sidebar Filters Tags - Grouped with Individual Delete 
              {Object.entries(sidebarFilters).some(([key, value]) => {
                if (!value) return false;
                if (key === "price") return value.min !== 10000 || value.max !== 150000;
                if (key === "featured") return true; // Presence of value means it's set to "yes" or "no"
                return value.length > 0;
              }) && (
                  <div className="bg-darkGraybg bg-opacity-60 rounded-lg p-3 sm:p-4 mx-2 sm:mx-4 mb-4">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(sidebarFilters).map(
                        ([filterType, selectedIds]) => {
                          // Handle different filter types
                          if (filterType === "featured") {
                            if (!selectedIds) return null;
                          } else if (filterType === "price") {
                            if (!selectedIds || (selectedIds.min === 10000 && selectedIds.max === 150000))
                              return null;
                          } else {
                            if (!selectedIds || selectedIds.length === 0)
                              return null;
                          }

                          const selectedNames = getSelectedFilterNames(
                            filterType,
                            selectedIds
                          );

                          return (
                            <div key={filterType} className="relative group">
                              <span className="inline-flex items-center bg-primary text-white px-3 py-1 rounded-full text-sm font-medium hover:bg-primary-dark transition-colors cursor-pointer">
                                <span className="font-semibold">
                                  {getFilterTypeDisplayName(filterType)}:
                                </span>
                                <span className="ml-1">
                                  {filterType === "featured" ? (
                                    selectedNames.join(", ")
                                  ) : selectedIds.length <= 1 ? (
                                    selectedNames.join(", ")
                                  ) : (
                                    <>
                                      {selectedNames.slice(0, 1).join(", ")}
                                      <span className="ml-1 text-xs bg-white text-primary px-1.5 py-0.5 rounded-full">
                                        +{selectedIds.length - 1} More
                                      </span>
                                    </>
                                  )}
                                </span>
                                <button
                                  onClick={() => clearFilterType(filterType)}
                                  className="ml-2 text-white hover:text-primary-light transition-colors"
                                  title={`Remove all ${getFilterTypeDisplayName(
                                    filterType
                                  ).toLowerCase()} filters`}
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </span>

                              {/* Dropdown for individual filter deletion 
                              {filterType !== "featured" &&
                                selectedIds.length > 1 && (
                                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-max opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                    <div className="p-2">
                                      <div className="text-xs font-semibold text-gray-700 mb-1 px-2">
                                        {translate(
                                          "search.removeIndividual"
                                        ).replace(
                                          "{filter}",
                                          getFilterTypeDisplayName(filterType)
                                        )}
                                      </div>
                                      {selectedIds.map((filterId, index) => (
                                        <button
                                          key={filterId}
                                          onClick={() =>
                                            removeSidebarFilter(
                                              filterType,
                                              filterId
                                            )
                                          }
                                          className={`flex items-center justify-between w-full px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors ${index >= 1 ? "bg-gray-50" : ""
                                            }`}
                                        >
                                          <span className="flex items-center gap-2">
                                            {getFilterName(
                                              filterType,
                                              filterId
                                            )}
                                          </span>
                                          <svg
                                            className="w-3 h-3 text-gray-400 hover:text-red-500"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>  
        </div>
        // /---------------------searchbar end here=============
     */}

        <div className="w-full bg-primary border-b-2 border-gray px-6 py-4 mb-8">
          <div className="max-w-[1600px] mx-auto">
            {/* Browse Results Heading */}
            <div id="browse-results" className="p-2 sm:p-4 pb-2">
              {/* Main Header Container: Always row, justify-between on all screens */}
              <div className="flex flex-row justify-between items-center gap-2 mb-4">
                {/* Title Section: Reduced font size on mobile (text-[14px]) */}
                <h2 className="sm:text-xl lg:text-2xl font-bold text-white whitespace-nowrap overflow-hidden text-ellipsis">
                  {translate("search.liveInventory") || "Live Inventory"}{" "}
                  <span className="text-description text-[10px] sm:text-[14px] font-normal">
                    (
                    {loading
                      ? "..."
                      : Object.keys(sidebarFilters).some(
                            (key) =>
                              sidebarFilters[key] &&
                              (Array.isArray(sidebarFilters[key])
                                ? sidebarFilters[key].length > 0
                                : sidebarFilters[key]),
                          )
                        ? vehicles.length
                        : totalVehicles}{" "}
                    {translate("search.available")})
                  </span>
                </h2>

                {/* Controls Section: Stay on same line, reduced sizes for mobile */}
                <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
                  {/* Auction dropdown — left of Sort */}
                  <div className="flex items-center gap-1">
                    <span className="sm:text-xl font-bold text-white whitespace-nowrap overflow-hidden text-ellipsis">
                      {translate('search.auctionText') || "Auction"}:
                    </span>
                    <div className="relative inline-grid items-center">
                      <select
                        value={selectedAuction}
                        onChange={(e) => handleAuctionChange(e.target.value)}
                        className="appearance-none row-start-1 col-start-1 w-full text-black/90 text-sm sm:text-[16px] font-medium border-none focus:ring-0 cursor-pointer h-[34px] pl-2 pr-6 rounded-[4px] outline-none text-black"
                      >
                        <option value="">
                          {translate("search.allAuctions") || "All Auctions"}
                        </option>
                        {auctions.map((auction) => {
                          const isLive =
                            auction.type === "live" ||
                            (auction.startDate &&
                              new Date(auction.startDate) <= new Date());
                          return (
                            // <option key={auction.id} value={auction.id.toString()}>
                            //   {isLive ? '🔴 ' : ''}{auction.name || `Auction #${auction.id}`}
                            // </option>
                            <option
                              key={auction.id}
                              value={auction.id.toString()}
                              style={{ color: isLive ? "green" : "gray-100" }}
                            >
                              {auction.name || `Auction #${auction.id}`}
                            </option>
                          );
                        })}
                      </select>
                      <div className="pointer-events-none absolute right-3 flex items-center">
                        <svg
                          className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-black/90"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <span className="sm:text-xl font-bold text-white whitespace-nowrap overflow-hidden text-ellipsis">
                      {translate('search.sortText') || "Sort"}:
                    </span>

                    <div className="relative inline-grid items-center">
                      <select
                        value={sortBy}
                        onChange={(e) => handleSortChange(e.target.value)}
                        className="appearance-none row-start-1 col-start-1 w-full text-black/90 text-xs sm:text-sm font-medium border-none focus:ring-0 cursor-pointer h-[34px] pl-2 pr-6 rounded-[4px] outline-none text-black"
                      >
                        <option value="run-asc">
                          {translate("search.sort.runAsc") || "Run: Ascending Order"}
                        </option>
                        <option value="run-desc">
                          {translate("search.sort.runDesc") || "Run: Descending Order"}
                        </option>
                        <option value="id-desc">
                          {translate("search.sort.newest") || "Newest First"}
                        </option>
                        <option value="id-asc">
                          {translate("search.sort.oldest") || "Oldest First"}
                        </option>
                        <option value="price-low">
                          {translate("search.sort.priceLow") || "Price: Low to High"}
                        </option>
                        <option value="price-high">
                          {translate("search.sort.priceHigh") || "Price: High to Low"}
                        </option>
                        <option value="year-new">
                          {translate("search.sort.yearNew") || "Year: Newest First"}
                        </option>
                        <option value="year-old">
                          {translate("search.sort.yearOld") || "Year: Oldest First"}
                        </option>
                        <option value="mileage-low">
                          {translate("search.sort.mileageLow") || "Mileage: Low to High"}
                        </option>
                        <option value="mileage-high">
                          {translate("search.sort.mileageHigh") || "Mileage: High to Low"}
                        </option>
                      </select>

                      {/* Mirror span for sizing */}
                      <span className="row-start-1 col-start-1 invisible whitespace-nowrap text-xs sm:text-sm font-medium pl-2 pr-6 text-black/90">
                        {sortBy === "run-asc" && (translate("search.sort.runAsc") || "Run: Ascending Order")}
                        {sortBy === "run-desc" && (translate("search.sort.runDesc") || "Run: Descending Order")}
                        {sortBy === "id-desc" && (translate("search.sort.newest") || "Newest First")}
                        {sortBy === "id-asc" && (translate("search.sort.oldest") || "Oldest First")}
                        {sortBy === "price-low" && (translate("search.sort.priceLow") || "Price: Low to High")}
                        {sortBy === "price-high" && (translate("search.sort.priceHigh") || "Price: High to Low")}
                        {sortBy === "year-new" && (translate("search.sort.yearNew") || "Year: Newest First")}
                        {sortBy === "year-old" && (translate("search.sort.yearOld") || "Year: Oldest First")}
                        {sortBy === "mileage-low" && (translate("search.sort.mileageLow") || "Mileage: Low to High")}
                        {sortBy === "mileage-high" && (translate("search.sort.mileageHigh") || "Mileage: High to Low")}
                      </span>

                      {/* Dropdown arrow moved slightly left */}
                      <div className="pointer-events-none absolute right-3 flex items-center">
                        <svg
                          className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-black/90"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Relative wrapper to position the custom arrow */}
                  {/* <div className="flex items-center gap-2">
  <span className="text-description text-[12px] font-medium">
    {translate("search.sort") || "Sort"}:
  </span>
  
  <div className="relative flex items-center">
    <select
      value={sortBy}
      onChange={(e) => handleSortChange(e.target.value)}
      /* Added w-fit and adjusted pr-4 to bring the arrow closer 
      className="appearance-none w-fit text-description text-[12px] font-medium bg-card border-none focus:ring-0 cursor-pointer py-1 pl-1 pr-4 rounded-[4px] outline-none"
    >
      <option value="id-desc">Newest First</option>
      <option value="id-asc">Oldest First</option>
      <option value="price-low">Price: Low to High</option>
      <option value="price-high">Price: High to Low</option>
      <option value="year-new">Year: Newest First</option>
      <option value="year-old">Year: Oldest First</option>
      <option value="mileage-low">Mileage: Low to High</option>
      <option value="mileage-high">Mileage: High to Low</option>
    </select>
    
    {/* Arrow positioned exactly at the edge of the text 
    <div className="pointer-events-none absolute right-0 flex items-center">
      <svg className="h-3 w-3 text-description" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>


</div> */}
                </div>
              </div>

              {/* Lane Tabs (per selected auction) */}
              {selectedAuction && (
               <div className="mt-3 flex flex-wrap gap-2 justify-end">
                  <button
                    className={`px-3 py-1.5 rounded-full border text-sm font-semibold ${
                      !selectedLane
                        ? "bg-white text-black border-white"
                        : "bg-white/10 text-white border-white/30 hover:bg-white/20"
                    }`}
                    onClick={() => {
                      setSelectedLane("");
                      setCurrentPage(1);
                    }}
                  >
                    All Lanes
                  </button>

                  {auctions
                    .find((auction) => auction.id.toString() === selectedAuction)
                    ?.auctionLanes?.map((lane) => {
                      const laneKey = (lane.id || lane.laneId || "").toString();
                      const laneName = lane.lane?.name || lane.name || `Lane ${lane.laneId || lane.id}`;
                      const isActive = selectedLane.toString() === laneKey;

                      return (
                        <button
                          key={laneKey}
                          className={`px-3 py-1.5 rounded-full border text-sm font-semibold ${
                            isActive
                              ? "bg-white text-black border-white"
                              : "bg-white/10 text-white border-white/30 hover:bg-white/20"
                          }`}
                          onClick={() => {
                            setSelectedLane(laneKey);
                            setCurrentPage(1);
                          }}
                        >
                          {laneName}
                        </button>
                      );
                    })}
                </div>
              )}

              {/* Selected Filters Display */}
              {(query ||
                Object.entries(sidebarFilters).some(([key, value]) => {
                  if (!value) return false;
                  if (key === "price") {
                    const bounds = sidebarFilterData.priceBounds || { min: 0, max: 1000000 };
                    return value.min !== bounds.min || value.max !== bounds.max;
                  }
                  if (key === "featured") return !!value;
                  return Array.isArray(value) && value.length > 0;
                })) && (
                <div className="mb-4 py-4">
                  <div className="flex flex-wrap gap-3 items-center">
                    {/* Search Query Tag */}
                    {query && (
                      <button
                        onClick={() => {
                          setQuery("");
                          const newParams = new URLSearchParams(searchParams);
                          newParams.delete("search");
                          setSearchParams(newParams);
                        }}
                        className="px-4 py-2 bg-soft text-primary rounded-full border border-primary text-sm text-description hover:border-primary-soft transition cursor-pointer flex items-center gap-2"
                      >
                        <span className="text-primary">×</span>
                        <span>Search: {query}</span>
                      </button>
                    )}

                    {/* Filter Buttons */}
                    {Object.entries(sidebarFilters).map(
                      ([filterType, selectedIds]) => {
                        // Skip empty filters
                        if (filterType === "featured") {
                          if (!selectedIds) return null;
                        } else if (filterType === "price") {
                          const bounds = sidebarFilterData.priceBounds || { min: 0, max: 1000000 };
                          if (
                            !selectedIds ||
                            (selectedIds.min === bounds.min &&
                              selectedIds.max === bounds.max)
                          )
                            return null;
                        } else {
                          if (!selectedIds || selectedIds.length === 0)
                            return null;
                        }

                        // Get filter names
                        const selectedNames = getSelectedFilterNames(
                          filterType,
                          selectedIds,
                        );
                        const displayName =
                          getFilterTypeDisplayName(filterType);

                        // Render individual filter buttons for arrays
                        if (
                          Array.isArray(selectedIds) &&
                          filterType !== "featured"
                        ) {
                          return selectedIds.map((filterId) => (
                            <button
                              key={`${filterType}-${filterId}`}
                              onClick={() =>
                                removeSidebarFilter(filterType, filterId)
                              }
                              className="px-4 py-2 bg-soft text-primary rounded-full border border-primary text-sm text-description hover:border-primary-soft transition cursor-pointer flex items-center gap-2"
                            >
                              <span>{getFilterName(filterType, filterId)}</span>
                              <span className="text-primary">×</span>
                            </button>
                          ));
                        }

                        // Render single filter buttons (fallback or special cases)
                        if (
                          !Array.isArray(selectedIds) &&
                          !["featured", "price"].includes(filterType)
                        ) {
                          if (!selectedIds) return null;
                          return (
                            <button
                              key={`${filterType}-${selectedIds}`}
                              onClick={() =>
                                removeSidebarFilter(filterType, selectedIds)
                              }
                              className="px-4 py-2 bg-soft text-primary rounded-full border border-primary text-sm text-description hover:border-primary-soft transition cursor-pointer flex items-center gap-2"
                            >
                              <span>
                                {getFilterName(filterType, selectedIds)}
                              </span>
                              <span className="text-primary">×</span>
                            </button>
                          );
                        }

                        // Render single filter button for featured and price
                        return (
                          <button
                            key={filterType}
                            onClick={() => clearFilterType(filterType)}
                            className="px-4 py-2 bg-soft text-primary rounded-full border border-primary text-sm text-description hover:border-primary-soft transition cursor-pointer flex items-center gap-2"
                          >
                            <span>
                              {displayName}: {selectedNames.join(", ")}
                            </span>
                            <span className="text-primary">×</span>
                          </button>
                        );
                      },
                    )}

                    {/* Clear All Button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        // Reset all filters including search
                        setSidebarFilters({});
                        setSidebarFilterData({});
                        setQuery("");
                        setFilters({
                          make: "",
                          model: "",
                          yearStart: "",
                          yearEnd: "",
                        });

                        // Clear URL parameters using setSearchParams
                        setSearchParams({});

                        if (
                          sidebarRef.current &&
                          sidebarRef.current.resetAllFilters
                        ) {
                          sidebarRef.current.resetAllFilters();
                        }
                      }}
                      className="px-4 py-2 rounded-full text-sm text-description hover:text-white hover:border-gray-400 transition flex items-center gap-2 font-medium"
                    >
                      <span>{translate("search.clearAll") || "Clear All"}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Separator Line */}
              {/* <hr className="border-primary-dark mb-4" /> */}

              {/* Auction and Lane Info Display */}

              {/* {(auctionId && laneId) || selectedAuction ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h3 className="text-lg font-semibold text-blue-800">
                      Auction Information
                    </h3>
                  </div>

                  {(() => {
                    const currentAuctionId = selectedAuction || auctionId;
                    const currentLaneId = selectedLane || laneId;

                    const selectedAuctionData = auctions.find(
                      (auction) => auction.id.toString() === currentAuctionId
                    );

                    if (!selectedAuctionData) return null;

                    // Log auction and lanes information
                    console.log("=== AUCTION AND LANES INFORMATION ===");
                    console.log("Current Auction ID:", currentAuctionId);
                    console.log("Current Lane ID:", currentLaneId);
                    console.log("Selected Auction Data:", selectedAuctionData);
                    console.log(
                      "Auction Name:",
                      selectedAuctionData.name || selectedAuctionData.auction
                    );
                    console.log("Auction Date:", selectedAuctionData.startDate);
                    console.log(
                      "Available Lanes:",
                      selectedAuctionData.auctionLanes
                    );

                    if (
                      selectedAuctionData.auctionLanes &&
                      selectedAuctionData.auctionLanes.length > 0
                    ) {
                      console.log("=== ALL LANES IN CURRENT AUCTION ===");
                      console.log(
                        "Total Lanes:",
                        selectedAuctionData.auctionLanes.length
                      );

                      // Create a table format for better readability
                      console.table(
                        selectedAuctionData.auctionLanes.map((lane, index) => ({
                          "#": index + 1,
                          "Auction Lane ID": lane.id,
                          "Lane ID": lane.laneId,
                          "Lane Name": lane.lane?.name || `Lane ${lane.laneId}`,
                          "Run Number": lane.runNo || "N/A",
                          "Vehicle Count":
                            lane.auctionLaneVehicles?.length || 0,
                          "Auction ID": lane.auctionId || currentAuctionId,
                          "Is Selected":
                            lane.id?.toString() === currentLaneId ||
                            lane.laneId?.toString() === currentLaneId
                              ? "YES"
                              : "NO",
                        }))
                      );

                      // Also log individual lane details
                      console.log("Individual Lane Details:");
                      selectedAuctionData.auctionLanes.forEach(
                        (lane, index) => {
                          const isSelected =
                            lane.id?.toString() === currentLaneId ||
                            lane.laneId?.toString() === currentLaneId;
                          console.log(
                            `Lane ${index + 1} ${
                              isSelected ? "(SELECTED)" : ""
                            }:`,
                            {
                              auctionLaneId: lane.id,
                              laneId: lane.laneId,
                              laneName:
                                lane.lane?.name || `Lane ${lane.laneId}`,
                              runNo: lane.runNo,
                              vehicleCount:
                                lane.auctionLaneVehicles?.length || 0,
                              auctionId: lane.auctionId || currentAuctionId,
                              isSelected: isSelected,
                              fullLaneObject: lane,
                            }
                          );
                        }
                      );
                      console.log("=====================================");
                    } else {
                      console.log("No lanes available for this auction");
                    }
                    console.log("=====================================");

                    return (
                      <div className="space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Auction:
                            </span>
                            <span className="text-blue-600">
                              {selectedAuctionData.name ||
                                selectedAuctionData.auction}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Date:
                            </span>
                            <span className="text-blue-600">
                              {new Date(
                                selectedAuctionData.startDate
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Auction ID:
                            </span>
                            <span className="text-blue-600">
                              {currentAuctionId}
                            </span>
                          </div>
                        </div>

                        {currentLaneId && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Selected Lane:
                            </span>
                            <span className="text-blue-600">
                              {(() => {
                                const lane =
                                  selectedAuctionData.auctionLanes?.find(
                                    (lane) =>
                                      lane.id?.toString() === currentLaneId ||
                                      lane.laneId?.toString() === currentLaneId
                                  );
                                return (
                                  lane?.lane?.name || `Lane ${currentLaneId}`
                                );
                              })()}
                            </span>
                            <span className="text-xs text-gray-500">
                              (ID: {currentLaneId})
                            </span>
                          </div>
                        )}

                        <div className="text-sm text-blue-600">
                          {currentLaneId
                            ? `Showing vehicles from the selected lane only`
                            : `Showing all vehicles from this auction`}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : null} */}

              {/* Auctions Dropdown and Lanes */}
              {/* <div className="mb-4">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <select
                    value={selectedAuction}
                    onChange={(e) => handleAuctionChange(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base text-gray-700 bg-white hover:border-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">All Auctions</option>
                    {auctions.length > 0 && (
                      <optgroup label="📅 Upcoming Auctions">
                        {auctions.map((auction) => (
                          <option key={auction.id} value={auction.id}>
                            {auction.name} -{" "}
                            {new Date(auction.startDate).toLocaleDateString()}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>

                  {/* Lanes Buttons 
                  {selectedAuction &&
                    (() => {
                      const selectedAuctionData = auctions.find(
                        (auction) => auction.id.toString() === selectedAuction
                      );

                      // Get lanes from the processed auctionLanes
                      let lanes = [];
                      if (
                        selectedAuctionData &&
                        selectedAuctionData.auctionLanes
                      ) {
                        lanes = selectedAuctionData.auctionLanes;
                      }

                      return (
                        <div className="flex flex-col gap-2">
                          {lanes.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {lanes.map((auctionLane) => {
                                const auctionLaneId = auctionLane.id; // This is the auctionLaneId for filtering
                                const laneId = auctionLane.laneId; // This is the actual laneId
                                const laneName =
                                  auctionLane.lane?.name ||
                                  `Lane ${auctionLane.laneId}`;
                                const runNo = auctionLane.runNo;

                                // Log the mapping for debugging
                                console.log("Lane Button Mapping:", {
                                  laneName: laneName,
                                  auctionLaneId: auctionLaneId,
                                  laneId: laneId,
                                  runNo: runNo,
                                  fullAuctionLaneObject: auctionLane,
                                });

                                return (
                                  <button
                                    key={auctionLaneId}
                                    onClick={() => {
                                      console.log("Lane Button Clicked:", {
                                        buttonLaneName: laneName,
                                        auctionLaneId: auctionLaneId,
                                        laneId: laneId,
                                        willCallHandleLaneSelectWith:
                                          auctionLaneId,
                                      });
                                      handleLaneSelect(auctionLaneId);
                                    }}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${selectedLane === auctionLaneId ||
                                      laneId === auctionLaneId?.toString() ||
                                      laneId === laneId?.toString()
                                      ? "bg-primary text-white hover:bg-primary-dark"
                                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                      }`}
                                    title={`Lane ID: ${laneId}, Auction Lane ID: ${auctionLaneId}, Run #${runNo}`}
                                  >
                                    {laneName}
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded">
                              No lanes available for this auction
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </div>
              </div> */}

              {/* <div className="w-full h-0.5 bg-primary mb-2 sm:mb-4"></div> */}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-start max-w-7xl mx-auto gap-4 px-2 sm:px-4 relative">
          {/* Mobile Burger Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="flex items-center justify-center p-2 bg-card border border-default rounded-lg text-white hover:bg-soft transition-colors"
            >
              <Menu size={24} className="text-primary" />
            </button>
          </div>

          {/* Sidebar Overlay for Mobile */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[60] lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Sidebar - Always visible on desktop, overlay on mobile */}
          <div
            id="left-section"
            className={`
              w-full lg:w-[20%]
              ${
                isSidebarOpen
                  ? "fixed inset-0 z-[70] bg-page overflow-y-auto p-0 h-[100dvh] w-full flex flex-col scroll-smooth overscroll-contain"
                  : "h-fit hidden lg:block"
              }
            `}
          >
            <Leftsidebar
              ref={sidebarRef}
              onFiltersChange={handleSidebarFiltersChange}
              onClose={() => setIsSidebarOpen(false)}
              isMobile={isSidebarOpen}
              vehicles={vehicles}
            />
          </div>

          <div
            className={`w-full ${isSidebarCollapsed ? "lg:w-[95%]" : "lg:w-[80%]"} transition-all duration-300`}
          >
            {/* Vehicle Results */}
            {loading ? (
              <div className="w-full p-8 flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading vehicles...</p>
                </div>
              </div>
            ) : !hasFetched ? null : currentVehicles.length === 0 ? (
              /* ✅ No Data (ONLY after fetch) */
              <div className="w-full min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
                <p
                  className={`text-description text-lg font-semibold ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {translate("search.messages.noVehiclesFound")}
                </p>
                <p
                  className={`text-gray-500 mt-2 ${
                    isRTL ? "text-right" : "text-left"
                  }`}
                >
                  {translate("search.messages.tryAdjustingFilters")}
                </p>
              </div>
            ) : viewMode === "grid" ? (
              /* ✅ Grid View */
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 p-2 sm:p-4">
                {currentVehicles.map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    wishlistData={wishlistData}
                  />
                ))}
              </div>
            ) : (
              /* ✅ List View */
              <div className="w-full p-2 sm:p-4">
                {currentVehicles.map((vehicle) => (
                  <VehicleListCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
            )}

            {currentPage < totalPages && (
              <div className="flex flex-col items-center px-2 sm:px-4 py-6 bg-page">
                {/* Load More Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={loading}
                  className={`px-20 py-3 text-sm font-semibold rounded-2xl transition-all duration-200 ${
                    loading
                      ? "bg-page text-white cursor-not-allowed"
                      : "bg-card text-white hover:bg-soft shadow-lg hover:shadow-xl active:scale-95"
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center gap-2"></div>
                  ) : (
                    "Load More"
                  )}
                </button>

                {/* Showing Info */}
                <div className="mt-4 text-sm text-description/80 font-medium uppercase">
                  Showing {endIndex} of {displayCount} Listing
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Search;
